import { useCallback, useRef, useState } from "react";

import { clamp } from "lodash-es";
import { useLocalStorage } from "usehooks-ts";

import { useEventListener } from "src/hooks/usehooks-ts";

type UseResizableWidthOptions = {
  // Viewport edge the pane is anchored to. Determines cursor math:
  // "right" → dragging left grows width; "left" → dragging right grows width.
  side: "left" | "right";
  storageKey: string;
  minWidth: number;
  maxWidth: number;
  defaultWidth?: number;
};

type UseResizableWidthResult = {
  width: number;
  isResizing: boolean;
  handleResizeStart: () => void;
};

export const useResizableWidth = ({
  side,
  storageKey,
  minWidth,
  maxWidth,
  defaultWidth,
}: UseResizableWidthOptions): UseResizableWidthResult => {
  const effectiveDefault = defaultWidth ?? (minWidth + maxWidth) / 2;
  const [width, setWidth] = useLocalStorage<number>(
    storageKey,
    effectiveDefault,
  );
  const clampedWidth = clamp(width, minWidth, maxWidth);
  const [isResizing, setIsResizing] = useState(false);
  const animationFrameRef = useRef<number | null>(null);

  const handleResizeStart = useCallback(() => {
    setIsResizing(true);
  }, []);

  useEventListener("mousemove", (event: MouseEvent) => {
    if (!isResizing) return;

    // Cancel any pending animation frame
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    // Schedule the update for the next animation frame
    animationFrameRef.current = requestAnimationFrame(() => {
      const raw =
        side === "right" ? window.innerWidth - event.clientX : event.clientX;
      setWidth(clamp(raw, minWidth, maxWidth));
      animationFrameRef.current = null;
    });
  });

  useEventListener("mouseup", (event: MouseEvent) => {
    if (!isResizing) return;

    // Clean up any pending animation frame
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // Apply the final position to avoid losing the last width update
    const raw =
      side === "right" ? window.innerWidth - event.clientX : event.clientX;
    setWidth(clamp(raw, minWidth, maxWidth));

    setIsResizing(false);
  });

  return { width: clampedWidth, isResizing, handleResizeStart };
};
